import React, { useMemo, useState } from 'react'
import { Table, Form } from 'react-bootstrap-v5'
import { PlanningTabProps, dateLabel, downloadCsv, forecastSources, matchesSearch, quantity, usePlanningReport } from '../helpers/PlanningHelper'
import { PlanningNotice, PlanningTiles, ProductName } from './PlanningParts'

type PlanView = 'products' | 'blends'

const PlanningProductionPlan: React.FC<PlanningTabProps> = ({ active, filters }) => {
	const [view, setView] = useState<PlanView>('products')
	const [showAll, setShowAll] = useState(false)
	const [search, setSearch] = useState('')

	const { loading, data, errors } = usePlanningReport(
		active,
		'plan',
		filters.idProjects
			? {
				idProjects: filters.idProjects,
				asOfDate: filters.asOfDate,
				method: filters.method,
				horizon: filters.horizon,
				safetyDays: filters.safetyDays,
				pendingDays: filters.pendingDays,
			}
			: null
	)

	const products = useMemo(() => {
		return (data?.products ?? []).filter((row: any) =>
			(showAll || row.toMake > 0) && matchesSearch(search, row.productName, row.productCode, row.itemName, row.categoryName)
		)
	}, [data, showAll, search])

	const blends = useMemo(() => {
		return (data?.blends ?? []).filter((row: any) => (showAll || row.toPackVolume > 0) && matchesSearch(search, row.itemName))
	}, [data, showAll, search])

	const isProduction = data?.mode === 'Production'
	const blendWord = !data || isProduction ? 'Blend' : 'Item'

	const exportCsv = () => {
		if (view === 'products') {
			downloadCsv(
				`production-plan-products-${data.asOfDate}.csv`,
				['Product', 'Product Code', blendWord, 'Pack Size', 'Unit', 'Forecast', 'To Deliver', 'Demand', 'Safety Stock', 'Stock', 'To Make', 'To Make Volume'],
				products.map((row: any) => [
					row.productName, row.productCode, row.itemName, row.packSize, row.unitName, row.forecast, row.pending, row.demand,
					row.safetyStock, row.stock, row.toMake, row.toMakeVolume,
				])
			)
			return
		}

		downloadCsv(
			`production-plan-blends-${data.asOfDate}.csv`,
			[blendWord, 'Unit', 'Products', 'Demand Volume', 'To Pack Volume', 'Waiting To Be Packed', 'To Blend Volume'],
			blends.map((row: any) => [row.itemName, row.unitName, row.products, row.demandVolume, row.toPackVolume, row.wipVolume, row.toBlendVolume])
		)
	}

	return (
		<>
			<p className='text-muted small mb-3'>
				What to make for the period. Demand is the larger of the forecast and the orders still to deliver.
				To make = demand + safety stock − stock on hand, in whole packs.
				{isProduction && ' To blend = volume to pack − volume already blended and waiting to be packed.'}
			</p>

			<div className='row align-items-end'>
				<div className='col-md-3'>
					<Form.Group className='mb-3' controlId='planView'>
						<Form.Label>Show</Form.Label>
						<Form.Select id='planView' value={view} onChange={(event) => setView(event.currentTarget.value as PlanView)}>
							<option value='products'>Packs to make, by product</option>
							<option value='blends'>{blendWord === 'Blend' ? 'Volume to blend, by blend' : 'Volume to make, by item'}</option>
						</Form.Select>
					</Form.Group>
				</div>
				<div className='col-md-4'>
					<Form.Group className='mb-3' controlId='planSearch'>
						<Form.Label>Search</Form.Label>
						<Form.Control type='text' placeholder='Product, code or blend' value={search} onChange={(event) => setSearch(event.target.value)} />
					</Form.Group>
				</div>
				<div className='col-md-3'>
					<Form.Check
						className='mb-4'
						type='checkbox'
						id='planShowAll'
						label='Also show lines with enough stock'
						checked={showAll}
						onChange={(event) => setShowAll(event.target.checked)}
					/>
				</div>
				<div className='col-md-2'>
					<div className='mb-3'>
						<button className='btn btn-sm btn-outline-primary p-3' onClick={exportCsv} disabled={(view === 'products' ? products : blends).length === 0}>
							<i className='fas fa-download me-1'></i>Excel (CSV)
						</button>
					</div>
				</div>
			</div>

			<PlanningNotice loading={loading} errors={errors} hasData={!!data} />

			{data && errors.length === 0 && (
				<>
					<div className='alert alert-secondary py-3'>
						Period: {dateLabel(data.asOfDate)} to {dateLabel(data.toDate)} ({data.horizon} days), stock as of {dateLabel(data.asOfDate)}.
						Forecast from {forecastSources(data.months)}.
						Safety stock {data.safetyDays} days; orders to deliver from the last {data.pendingDays || 'all'} days.
					</div>

					<PlanningTiles
						tiles={[
							{ label: 'Demand volume', value: quantity(data.totals.demandVolume), hint: 'Forecast or orders to deliver, whichever is larger' },
							{ label: 'Of it on order', value: quantity(data.totals.pendingVolume), hint: 'Ordered and not yet delivered' },
							{ label: 'Stock volume', value: quantity(data.totals.stockVolume), hint: 'Finished stock of the products in demand' },
							{ label: 'Packs to make', value: quantity(data.totals.toMake) },
							{ label: 'Volume to make', value: quantity(data.totals.toMakeVolume) },
							isProduction
								? { label: 'Volume to blend', value: quantity(data.totals.toBlendVolume), hint: `After ${quantity(data.totals.wipVolume)} waiting to be packed` }
								: { label: 'Products in demand', value: String(data.products.length) },
						]}
					/>

					{view === 'products' && (
						<Table responsive striped bordered hover size='sm' className='mb-0 bg-white'>
							<thead className='table-light'>
								<tr>
									<th>SL</th>
									<th>Product</th>
									<th>Pack Size</th>
									<th>Unit</th>
									<th className='text-end'>Forecast</th>
									<th className='text-end'>To Deliver</th>
									<th className='text-end'>Demand</th>
									<th className='text-end'>Safety Stock</th>
									<th className='text-end'>Stock</th>
									<th className='text-end'>To Make</th>
									<th className='text-end'>To Make Volume</th>
								</tr>
							</thead>
							<tbody>
								{products.length === 0 && (
									<tr>
										<td colSpan={11} className='text-center text-muted py-4'>
											{data.products.length === 0 ? 'No forecast and no orders to deliver for this period.' : 'Stock covers the demand of every product shown.'}
										</td>
									</tr>
								)}
								{products.map((row: any, index: number) => (
									<tr key={row.idProducts}>
										<td>{index + 1}</td>
										<td><ProductName row={row} /></td>
										<td>{row.packSize}</td>
										<td>{row.unitName}</td>
										<td className='text-end'>{quantity(row.forecast, 2)}</td>
										<td className='text-end'>{row.pending > 0 ? quantity(row.pending, 2) : ''}</td>
										<td className='text-end'>{quantity(row.demand, 2)}</td>
										<td className='text-end'>{row.safetyStock > 0 ? quantity(row.safetyStock, 2) : ''}</td>
										<td className={`text-end ${row.stock < 0 ? 'text-danger' : ''}`} title={row.stock < 0 ? 'Negative stock is counted as none' : ''}>{quantity(row.stock, 2)}</td>
										<td className='text-end fw-bold'>{row.toMake > 0 ? quantity(row.toMake) : ''}</td>
										<td className='text-end'>{row.toMake > 0 ? `${quantity(row.toMakeVolume)} ${row.itemUnitName}` : ''}</td>
									</tr>
								))}
							</tbody>
							{products.length > 0 && (
								<tfoot>
									<tr>
										<th colSpan={9} className='text-end'>Total of the lines shown</th>
										<th className='text-end'>{quantity(products.reduce((sum: number, row: any) => sum + row.toMake, 0))}</th>
										<th className='text-end'>{quantity(products.reduce((sum: number, row: any) => sum + row.toMakeVolume, 0))}</th>
									</tr>
								</tfoot>
							)}
						</Table>
					)}

					{view === 'blends' && (
						<Table responsive striped bordered hover size='sm' className='mb-0 bg-white'>
							<thead className='table-light'>
								<tr>
									<th>SL</th>
									<th>{blendWord}</th>
									<th>Unit</th>
									<th className='text-end'>Products</th>
									<th className='text-end'>Demand Volume</th>
									<th className='text-end'>To Pack Volume</th>
									<th className='text-end'>Waiting To Be Packed</th>
									<th className='text-end'>To Blend Volume</th>
								</tr>
							</thead>
							<tbody>
								{blends.length === 0 && (
									<tr>
										<td colSpan={8} className='text-center text-muted py-4'>
											{data.blends.length === 0 ? 'No forecast and no orders to deliver for this period.' : 'Stock covers the demand of every blend shown.'}
										</td>
									</tr>
								)}
								{blends.map((row: any, index: number) => (
									<tr key={row.key}>
										<td>{index + 1}</td>
										<td>{row.itemName}</td>
										<td>{row.unitName}</td>
										<td className='text-end'>{row.products}</td>
										<td className='text-end'>{quantity(row.demandVolume)}</td>
										<td className='text-end'>{quantity(row.toPackVolume)}</td>
										<td className='text-end'>{row.wipVolume > 0 ? quantity(row.wipVolume, 2) : ''}</td>
										<td className='text-end fw-bold'>{row.toBlendVolume > 0 ? quantity(row.toBlendVolume) : ''}</td>
									</tr>
								))}
							</tbody>
							{blends.length > 0 && (
								<tfoot>
									<tr>
										<th colSpan={5} className='text-end'>Total of the lines shown</th>
										<th className='text-end'>{quantity(blends.reduce((sum: number, row: any) => sum + row.toPackVolume, 0))}</th>
										<th className='text-end'>{quantity(blends.reduce((sum: number, row: any) => sum + row.wipVolume, 0), 2)}</th>
										<th className='text-end'>{quantity(blends.reduce((sum: number, row: any) => sum + row.toBlendVolume, 0))}</th>
									</tr>
								</tfoot>
							)}
						</Table>
					)}
				</>
			)}
		</>
	)
}

export default PlanningProductionPlan
